import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';

import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildLineAreaOption } from './unified-line-area-option.builder';
import type { LineAreaWidgetData } from './unified-line-area-chart.types';

/**
 * Unified Line & Area chart — ONE ECharts component for the whole family
 * (Simple / Multi / Step / Spline lines; Simple / Stacked / 100% / Spline areas)
 * plus dual axes and analytics overlays (Trend / Forecast / Moving Average).
 *
 * All variation lives in `data.capabilities` (see LineAreaCapabilities); the
 * PURE `buildLineAreaOption` turns capabilities + flat rows into the option and
 * this component is just the lifecycle shell (init / dispose / resize / theme),
 * mirroring the Bar & Column family. Reads pre-fetched rows from
 * `staticData` / `rows` / `data` or self-fetches via ConfigService.getConfig.
 */
@Component({
  selector: 'app-unified-line-area-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './unified-line-area-chart.component.html',
  styleUrl: './unified-line-area-chart.component.scss',
})
export class UnifiedLineAreaChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: LineAreaWidgetData | Record<string, unknown>;

  /** Emitted on point click when `capabilities.emitSelection` is on. */
  @Output() selection = new EventEmitter<{ category: string; series?: string; value: number }>();

  /** Emitted on point click when `capabilities.crossFilter` is on — the column +
   *  the FULL set of currently-selected values (multi-select toggle). */
  @Output() crossFilterChange = new EventEmitter<{ column: string; value: string | null; values: string[] }>();

  protected myChart!: echarts.ECharts;
  private highlighted = new Set<string>();
  private crossFilterValues = new Set<string>();

  @ViewChild('ulaHost', { static: false }) private hostRef?: ElementRef<HTMLDivElement>;

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private lastRows: Array<Record<string, unknown>> = [];
  /** Host width the current option was built at — see the ResizeObserver. */
  private lastBuiltWidth = 0;

  private get cfg(): LineAreaWidgetData {
    return (this.data || {}) as LineAreaWidgetData;
  }

  get hostHeight(): string {
    const h = this.cfg.height;
    if (h === undefined || h === null || h === '') return 'var(--dvm-chart-height, 300px)';
    if (typeof h === 'number') return `${h}px`;
    return /^\d+$/.test(String(h)) ? `${h}px` : String(h);
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => [],
    });

    const staticRows = this.resolveStaticRows();
    if (staticRows) {
      setTimeout(() => this.renderRows(staticRows), 0);
    } else {
      setTimeout(() => this.loadAndRenderChart(initialFilters ?? undefined), 0);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    const host = this.resolveHost();
    if (host && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.myChart?.resize();
        // The category-label step is baked into the option at build time, so a
        // width change has to REBUILD or the axis keeps a density picked for the
        // old size. Only on a material change — resize() alone handles the rest.
        const w = this.myChart?.getWidth() || 0;
        if (w && this.lastBuiltWidth && Math.abs(w - this.lastBuiltWidth) / this.lastBuiltWidth > 0.15) {
          this.renderRows(this.lastRows);
        }
      });
      this.resizeObserver.observe(host);
    }

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.myChart || !this.lastRows.length) return;
      const chartDom = this.resolveHost();
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderRows(this.lastRows);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].isFirstChange()) return;
    if (!this.myChart) return;
    const staticRows = this.resolveStaticRows();
    this.renderRows(staticRows ?? this.lastRows);
  }

  private resolveHost(): HTMLElement | null {
    return this.hostRef?.nativeElement || document.getElementById(this.cfg.widgetId || '');
  }

  private resolveStaticRows(): Array<Record<string, unknown>> | null {
    const candidate = this.cfg.staticData ?? this.cfg.rows ?? this.cfg.data;
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    const nested = (candidate as { data?: unknown })?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : null;
  }

  protected loadAndRenderChart(filters?: unknown): void {
    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    const staticRows = this.resolveStaticRows();
    if (staticRows) {
      this.renderRows(staticRows);
      return;
    }
    if (!this.cfg.apiUrl) {
      this.renderRows([]);
      return;
    }

    this.myChart.showLoading();
    this.configService.getConfig(this.cfg.apiUrl, this.data, filters)?.subscribe({
      next: (res: unknown) => {
        const raw = (res as { data?: unknown })?.data ?? res;
        this.renderRows(Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : []);
        this.myChart.hideLoading();
      },
      error: () => this.myChart.hideLoading(),
    });
  }

  private renderRows(rows: Array<Record<string, unknown>>): void {
    const capabilities = this.cfg.capabilities;
    if (!capabilities) return;
    this.lastRows = rows;

    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    // Width feeds the shared category-label step (labels + secondary-X cells
    // must thin together). getWidth() is 0 before the first layout pass, so
    // fall back to the host box.
    const hostWidthPx = this.myChart.getWidth() || chartDom.clientWidth || chartDom.getBoundingClientRect().width;
    this.lastBuiltWidth = hostWidthPx;
    const option = buildLineAreaOption(rows, capabilities, hostWidthPx) as Record<string, unknown>;
    applyRoleVisibilityToOption(option, this.data);
    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.myChart.resize();
    setTimeout(() => this.myChart?.resize(), 60);
    this.addResizeListener();
    this.wrapper.afterRender();
    this.myChart.off('legendselectchanged');
    this.setupInteractions();
  }

  private setupInteractions(): void {
    const cap = this.cfg.capabilities;
    if (!cap || !this.myChart) return;
    const anyClick = cap.highlightOnClick || cap.crossFilter || cap.emitSelection;
    if (!anyClick) return;
    this.myChart.off('click');

    this.myChart.on('click', (params: { seriesType?: string; name?: string; seriesName?: string; value?: unknown }) => {
      if (params.seriesType !== 'line') return;
      const category = String(params.name ?? '');
      const value = Number(params.value ?? 0);

      if (cap.emitSelection) {
        this.selection.emit({ category, series: cap.series ? params.seriesName : undefined, value });
      }
      if (cap.highlightOnClick) {
        this.toggleHighlight(category);
      }
      if (cap.crossFilter) {
        const column = cap.crossFilterKey || cap.category;
        if (this.crossFilterValues.has(category)) this.crossFilterValues.delete(category);
        else this.crossFilterValues.add(category);
        const values = Array.from(this.crossFilterValues);
        this.applyCrossFilterEmphasis();
        this.crossFilterChange.emit({ column, value: category, values });
      }
    });
  }

  private toggleHighlight(category: string): void {
    if (this.highlighted.has(category)) this.highlighted.delete(category);
    else this.highlighted.add(category);
    this.myChart.dispatchAction({ type: 'downplay' });
    if (this.highlighted.size > 0) {
      const opt = this.myChart.getOption() as { xAxis?: Array<{ data?: string[] }> };
      const axisData = opt.xAxis?.[0]?.data || [];
      axisData.forEach((name, dataIndex) => {
        if (this.highlighted.has(String(name))) {
          this.myChart.dispatchAction({ type: 'highlight', dataIndex });
        }
      });
    }
  }

  private applyCrossFilterEmphasis(): void {
    if (!this.myChart) return;
    this.myChart.dispatchAction({ type: 'downplay' });
    if (this.crossFilterValues.size === 0) return;
    const opt = this.myChart.getOption() as { xAxis?: Array<{ data?: string[] }> };
    const axisData = opt.xAxis?.[0]?.data || [];
    axisData.forEach((name, dataIndex) => {
      if (this.crossFilterValues.has(String(name))) {
        this.myChart.dispatchAction({ type: 'highlight', dataIndex });
      }
    });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
